import Spinner from '@alexis/components/Spinner';
import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { generateCUID } from '@alexis/helpers/identity';
import FlightPerLegSearchStepper from '@pages/FlightSearchResult/components/FlightPerLegSearchStepper';
import FlightPerLegSearchProvider from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import useFlightResultGenzoTracking from '@pages/FlightSearchResult/hooks/useFlightResultGenzoTracking';
import useFlightSearchAds from '@pages/FlightSearchResult/hooks/useFlightSearchAds';
import useFlightSearchGTMTracking from '@pages/FlightSearchResult/hooks/useFlightSearchGTMTracking';
import useFlightSearchWebEngageTracking from '@pages/FlightSearchResult/hooks/useFlightSearchWebEngageTracking';
import useHotelCrossSell from '@pages/FlightSearchResult/hooks/useHotelCrossSell';
import { FlightSortType, FlightSelectedSortTypeForGenzoTracking } from 'flights/flightSortType';
import type { TripViewModel } from 'flights/tripViewModel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { SortOrder } from 'sortOrder';

import AdsBlockerBait from '@components/AdsBlockerBait';
import AlertMessage from '@components/AlertMessage/AlertMessage';
import BackToTopButton from '@components/BackToTopButton';
import FlightResultRHS from '@components/flights/FlightResultRHS';
import FlightSearchResultContentAds from '@components/flights/FlightSearchResultContentAds';
import FlightSort from '@components/flights/FlightSort';
import ProgressBar from '@components/flights/ProgressBar';
import TripDetail from '@components/flights/TripDetail';
import HotelCrossSell from '@components/hotels/HotelCrossSell';
import NoResults from '@components/NoResults';
import RefreshPopup from '@components/RefreshPopup';
import TurnstileWidget from '@components/TurnstileWidget';

import { GTM_ID } from '@constants/general';

import { useFlightContext } from '@context/FlightContext';
import { useSprinklrChat } from '@context/Sprinklrchat';

import {
  convertFilterStateToDataForTracking,
  deleteFilterStatesFromSearchParam,
  getMinMaxRangeOfASliderFilterUniqueForEachLeg,
  getSelectedRangeOfASliderFilterUniqueForEachLeg,
  getSelectedRangeOfTransitDurationFilter,
  getSelectedTimeBlocksForGenzoTracking,
  getTransitDurationMinMaxForLegsWithFilterApplied,
  saveFlightRecentSearchForComparing,
  getGenzoValuesForBookingOptionsFilter,
} from '@helpers/flight';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';
import { flightsRoute } from '@helpers/routeTranslation';
import { getErrorAlertContents, isValidDate } from '@helpers/utils';

import { useFlightSearchResultImpressionLogging } from '@hooks/useFlightSearchResultImpressionLogging';
import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { changeHomePageViewId } from '@redux/actions/pageViewIdActions';
import { updateTriggerSearchCounter } from '@redux/actions/triggerSearchCounterActions';
import {
  getCurrencyState,
  getCurrentSiteState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
  getTriggerSearchCounterState,
  getUserState,
} from '@redux/selectors';

import type { AdPositionConfig } from '@wegoTypes/adPositionConfig';
import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import FlightFilters from './components/FlightFilters';
import FlightSearchResultList from './components/FlightSearchResultList';
import SortAndFilterBar from './components/SortAndFilterBar';
import FlightSearchFiltersProvider from './contexts/FlightSearchFiltersContext';
import FlightSearchResultsProvider, {
  useFlightSearchResults,
} from './contexts/FlightSearchResultsContext';
import {
  copyFilterParamsFromDepartLegToReturnLeg,
  removeReturnLegFilterParams,
} from './flightPerLegSearchFilter';
import {
  copySortParamsFromDepartLegToReturnLeg,
  removeReturnLegSortParams,
} from './flightPerLegSearchSort';
import styles from './FlightSearchResult.module.scss';
import useFlightSearchFilters from './hooks/useFlightSearchFilters';
import useFlightSearchPageParams from './hooks/useFlightSearchPageParams';
import useFlightSearchSort from './hooks/useFlightSearchSort';
import useFlightSearchViewGenzoTracking from './hooks/useFlightSearchViewGenzoTracking';
import { SliderFiltersUniqueForEachLeg } from './types';

interface FlightSearchResultProps {
  availablePaymentMethods: Array<HomepageFlightsPaymentMethod>;
  gtmAuth: string;
  gtmPreview: string;
  localeParam: string | undefined;
  nearestCityByIP: Place | undefined;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
  onSearchIdChange?: (searchId: string) => void;
}

const FlightSearchResult$: React.FC<FlightSearchResultProps> = ({
  availablePaymentMethods,
  gtmAuth,
  gtmPreview,
  localeParam,
  nearestCityByIP,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  onSearchIdChange,
}) => {
  const { isFlightSearch2AndNotMulticity } = useFlightContext();

  const currentSite = useSelector(getCurrentSiteState);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const priceOptions = useSelector(getPriceOptionsState);
  const translations = useSelector(getTranslationsState);
  const user = useSelector(getUserState);
  const triggerSearchCounter = useSelector(getTriggerSearchCounterState);

  const { isPLSDepartLegSelection, isPLSChangeLegLoading, isPerLegSearch } = useFlightPLS();

  const {
    pricesFilter,
    stopCodesFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    providersFilter,
    flightExperiencesFilter,
    providerCodesFilter,
    tripsFilter,
    fallbackFilter,
    stopoverAirportCodesFilter,
    stopoverOptionsFilter,
    originAirportCodesFilter,
    destinationAirportCodesFilter,
    setSelectedLegs,
    activeLeg,
    setActiveLeg,
  } = useFlightSearchFilters();

  const { sortOrder, sortType, changeSort } = useFlightSearchSort();

  const {
    flightMetaSearchError,
    flightMetaSearchOriginalError,
    isLoading,
    showTurnstileWidget,
    legFilterOptions,
    filteredSponsoredAdTripViewModels,
    finalTripViewModelsToShowBasedOnSortType,
    groupedTripViewModelsDetail,
    sortedAndFilteredTripViewModels,
    bestExperienceCheapestFareViewModel,
    cheapestFareViewModel,
    minUsdPrice,
    maxUsdPrice,
    tripViewModels,
    search,
    setLeaderboardAdsAndInlineAdsConfig,
  } = useFlightSearchResults();

  useEffect(() => {
    if (search?.id && onSearchIdChange) {
      onSearchIdChange(search.id);
    }
  }, [search?.id]);

  const { isMultiCity, isRoundTrip, legs, passengerCount, paymentMethods } =
    useFlightSearchPageParams();

  const { sprinklrChat } = useSprinklrChat();

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  useEffect(() => {
    if (!pageViewId) return;

    dispatch(changeHomePageViewId(pageViewId));
  }, [pageViewId]);

  const { recordChangeSortAction, abortFlightSearchResultRecordings } =
    useFlightSearchResultsDatadog();

  const [visibleTripCardForUngroupedTrips, setVisibleTripCardForUngroupedTrips] = useState<
    TripViewModelDataForGenzo[]
  >([]);
  const [visibleTripCardForGroupedTrips, setVisibleTripCardForGroupedTrips] = useState<
    TripViewModelDataForGenzo[]
  >([]);

  const [isSearchExpired, setIsSearchExpired] = useState<boolean>(false);

  const [selectedTripViewModel, setSelectedTripViewModel] = useState<TripViewModel | undefined>();

  const [bnplToolTipShown, setBnplTooltipShown] = useState<boolean>(false);

  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { state } = useLocation();

  const navigate = useNavigate();

  const { showCrossSellPopup, setShowCrossSellPopup } = useHotelCrossSell(isSearchExpired);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTripIdSearchParam = searchParams.get('selected_trip_id');

  const hasEgyptCentralBankNotice = currentSite.countryCode === 'EG';

  const isDatesValid = legs.every((leg) => isValidDate(leg.outboundDate));

  const errorAlertContents = useMemo(
    () => getErrorAlertContents(flightMetaSearchError, isDatesValid, translations),
    [flightMetaSearchError, isDatesValid, translations],
  );

  const showErrorMessage = useMemo<boolean>(() => {
    return !!errorAlertContents && !!flightMetaSearchError.message && tripViewModels.length === 0;
  }, [flightMetaSearchError.message, tripViewModels.length, errorAlertContents]);

  const selectedFiltersObject = useMemo(() => {
    const bookingOptionsForTracking = providersFilter.map((filter) => ({
      ...filter,
      values: getGenzoValuesForBookingOptionsFilter(filter.values),
    }));

    const selectedOriginAirportCodesFilter =
      convertFilterStateToDataForTracking(
        originAirportCodesFilter.length ? originAirportCodesFilter : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ) || [];
    const selectedDestinationAirportCodes = convertFilterStateToDataForTracking(
      destinationAirportCodesFilter.length ? destinationAirportCodesFilter : fallbackFilter,
      false,
      true,
      isPerLegSearch,
      activeLeg,
    );

    const originDestinationAirportCodesFilter = selectedOriginAirportCodesFilter.concat(
      selectedDestinationAirportCodes,
    );

    const selectedFilters: {
      [key: string]:
        | string
        | number
        | Array<string | number | undefined>
        | GenzoRangeOfSliderFilter
        | GenzoFlightTimeFilter
        | undefined;
    } = {
      stops: convertFilterStateToDataForTracking(
        stopCodesFilter.length ? stopCodesFilter : fallbackFilter,
        isMultiCity,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      airlines: convertFilterStateToDataForTracking(
        airlineCodesFilter.length ? airlineCodesFilter : fallbackFilter,
        isMultiCity,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      itinerary: convertFilterStateToDataForTracking(
        flightExperiencesFilter.length ? flightExperiencesFilter : fallbackFilter,
        isMultiCity,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      alliances: convertFilterStateToDataForTracking(
        allianceCodesFilter.length ? allianceCodesFilter : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      booking_options: convertFilterStateToDataForTracking(
        bookingOptionsForTracking.length ? bookingOptionsForTracking : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      booking_sites: convertFilterStateToDataForTracking(
        providerCodesFilter.length ? providerCodesFilter : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      complete_trip_same_airline: tripsFilter.length ? 1 : 0,
      from_to_airports: originDestinationAirportCodesFilter,
      transit_airports: convertFilterStateToDataForTracking(
        stopoverAirportCodesFilter.length ? stopoverAirportCodesFilter : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      no_change_airports_stopover: convertFilterStateToDataForTracking(
        stopoverOptionsFilter.length ? stopoverOptionsFilter : fallbackFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
      price_options: priceOptions.showTotalPrice ? 'total_price' : '', // non-default value set to falsy value
      price_min_max_usd: pricesFilter.length ? [minUsdPrice, maxUsdPrice] : undefined,
      price_selected_usd: convertFilterStateToDataForTracking(
        pricesFilter.length ? pricesFilter : fallbackFilter,
        isPerLegSearch,
        true,
      ),
      totalduration_min_max: getMinMaxRangeOfASliderFilterUniqueForEachLeg(
        searchParams.get(FlightSearchResultSearchParams.DepartDuration),
        searchParams.get(FlightSearchResultSearchParams.ReturnDuration),
        SliderFiltersUniqueForEachLeg.TotalDurationFilter,
        isRoundTrip,
        legFilterOptions,
        isPerLegSearch,
        activeLeg,
      ),
      totalduration_selected: getSelectedRangeOfASliderFilterUniqueForEachLeg(
        searchParams.get(FlightSearchResultSearchParams.DepartDuration),
        searchParams.get(FlightSearchResultSearchParams.ReturnDuration),
        isRoundTrip,
        isPerLegSearch,
        activeLeg,
      ),
      transitduration_min_max: getTransitDurationMinMaxForLegsWithFilterApplied(
        searchParams.get(FlightSearchResultSearchParams.StopoverDuration),
        isRoundTrip,
        legFilterOptions,
        isPerLegSearch,
        activeLeg,
      ),
      transitduration_selected: getSelectedRangeOfTransitDurationFilter(
        searchParams.get(FlightSearchResultSearchParams.StopoverDuration),
        isRoundTrip,
        isPerLegSearch,
        activeLeg,
      ),
      departuretime_box_selected: getSelectedTimeBlocksForGenzoTracking(
        searchParams.get(FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks),
        searchParams.get(FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks),
        isRoundTrip,
        isPerLegSearch && activeLeg === 1,
      ),
      arrivaltime_box_selected: getSelectedTimeBlocksForGenzoTracking(
        searchParams.get(FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks),
        searchParams.get(FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks),
        isRoundTrip,
        isPerLegSearch && activeLeg === 1,
      ),
    };

    // remove key-value pair when value is falsy or an array of length 0
    Object.keys(selectedFilters).forEach((key) => {
      const value = selectedFilters[key];
      if (
        (Array.isArray(value) &&
          (value.length === 0 || !value.every((value) => value !== undefined))) ||
        !value
      ) {
        delete selectedFilters[key];
      }
    });

    return selectedFilters;
  }, [
    activeLeg,
    airlineCodesFilter,
    allianceCodesFilter,
    destinationAirportCodesFilter,
    fallbackFilter,
    flightExperiencesFilter,
    isMultiCity,
    isPerLegSearch,
    isRoundTrip,
    legFilterOptions,
    maxUsdPrice,
    minUsdPrice,
    originAirportCodesFilter,
    priceOptions.showTotalPrice,
    pricesFilter,
    providerCodesFilter,
    providersFilter,
    searchParams,
    stopCodesFilter,
    stopoverAirportCodesFilter,
    stopoverOptionsFilter,
    tripsFilter.length,
  ]);

  const [appliedFilters, setAppliedFilters] = useState(
    Object.keys(selectedFiltersObject).length === 0 ? '' : JSON.stringify(selectedFiltersObject),
  );

  // Leaderboards, native and RHS ads
  const {
    bottomLeaderboardAdsHeight,
    hasMrecOrTopStickyAds,
    middleLeaderboardAdsHeight,
    topLeaderboardAdsHeight,
    nativeBottomAdHeight,
    nativeMiddleAdHeight,
    nativeTopAdHeight,
  } = useFlightSearchAds();

  // Update leaderboard ads and inline ads here
  const leaderboardAdsAndInlineAdsConfig = useMemo<AdPositionConfig[]>(() => {
    const adConfigs: AdPositionConfig[] = [
      {
        index: 0,
        isSkip: false,
        visible: !!filteredSponsoredAdTripViewModels?.[0],
        type: 'inline',
        adItem: filteredSponsoredAdTripViewModels?.[0],
        id: '1st_inline',
      },
      {
        index: 3,
        isSkip: false,
        visible: !!nativeTopAdHeight || !!topLeaderboardAdsHeight,
        type: !!nativeTopAdHeight ? 'native' : 'leaderboard',
        id: 'top_leaderboard',
      },
      {
        index: 6,
        isSkip: false,
        visible: !!filteredSponsoredAdTripViewModels?.[1],
        type: 'inline',
        adItem: filteredSponsoredAdTripViewModels?.[1],
        id: '2nd_inline',
      },
      {
        index: 7,
        isSkip: false,
        visible: !!nativeMiddleAdHeight || !!middleLeaderboardAdsHeight,
        type: !!nativeMiddleAdHeight ? 'native' : 'leaderboard',
        id: 'middle_leaderboard',
      },
      {
        index: 9,
        isSkip: false,
        visible: !!filteredSponsoredAdTripViewModels?.[2],
        type: 'inline',
        adItem: filteredSponsoredAdTripViewModels?.[2],
        id: '3nd_inline',
      },
      {
        index: 10,
        isSkip: false,
        visible: !!nativeBottomAdHeight || !!bottomLeaderboardAdsHeight,
        type: !!nativeBottomAdHeight ? 'native' : 'leaderboard',
        id: 'bottom_leaderboard',
      },
    ];

    if (isFlightSearch2AndNotMulticity) {
      return adConfigs
        .filter((adConfig) => adConfig.type === 'inline')
        .toSorted((a, b) => a.index - b.index);
    }

    return adConfigs.toSorted((a, b) => a.index - b.index);
  }, [
    bottomLeaderboardAdsHeight,
    filteredSponsoredAdTripViewModels,
    middleLeaderboardAdsHeight,
    nativeBottomAdHeight,
    nativeMiddleAdHeight,
    nativeTopAdHeight,
    topLeaderboardAdsHeight,
    isFlightSearch2AndNotMulticity,
  ]);

  useEffect(() => {
    setLeaderboardAdsAndInlineAdsConfig(leaderboardAdsAndInlineAdsConfig);
  }, [leaderboardAdsAndInlineAdsConfig]);

  const handleCloseTripDetailPopup = useCallback<() => void>(() => {
    searchParams.delete('selected_trip_id');
    searchParams.delete('search_id');

    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleScrollToTop = useCallback(() => {
    if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'flights_search_results',
        object: 'bottom_right',
        action: 'go_to_top',
        value: 'click',
      };

      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
        generateCUID(),
      );
    }
  }, [pageViewId, user?.userHash, wegoAnalyticsClientId, wegoAnalyticsClientSessionId]);

  // Log genzo action with sprinklrChat
  useEffect(() => {
    const handleSprinklrEvent = (event: any) => {
      if (event?.response?.data?.eventType === 'CONVERSATION_WINDOW_OPENED') {
        if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
          const eventData = {
            id: pageViewId,
            category: 'flights_search_results',
            object: 'bottom_right',
            action: 'customer_service',
            value: 'click',
          };
          genzoTrackActionEvent(
            API_BASE_URL,
            wegoAnalyticsClientId,
            wegoAnalyticsClientSessionId,
            user?.userHash,
            eventData,
            generateCUID(),
          );
        }
      }
    };

    sprinklrChat('subscribeToUpdate', {
      topic: 'eventTriggered',
      subscriber: handleSprinklrEvent,
    });

    return () => {
      sprinklrChat('unsubscribeToUpdate', {
        topic: 'eventTriggered',
        subscriber: handleSprinklrEvent,
      });
    };
  }, [sprinklrChat]);

  //#region Impression Logs
  const impressionLogging = useFlightSearchResultImpressionLogging(
    appliedFilters,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    groupedTripViewModelsDetail,
    sortType,
    sortOrder,
    pageViewId,
    sortedAndFilteredTripViewModels,
    user,
  );
  //#endregion

  const genzoTrackApplyFilterActionEvent = useCallback(
    (
      eventDataObjectType: string,
      eventDataActionType: string,
      eventDataPair: { [key: string]: any },
    ): void => {
      if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
        let eventDataValueObject: any = {};

        // merge with currently applied filter key-value pair
        eventDataValueObject = { ...selectedFiltersObject, ...eventDataPair };

        const eventData: any = {
          id: pageViewId,
          category: 'search_results_options',
          object: eventDataObjectType,
          action: eventDataActionType,
          value:
            Object.keys(eventDataValueObject).length === 0 || eventDataActionType === 'reset'
              ? ''
              : JSON.stringify(eventDataValueObject),
        };

        setAppliedFilters(eventData.value);

        const eventId = generateCUID();

        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          user?.userHash,
          eventData,
          eventId,
        );

        if (eventDataObjectType === 'sort') {
          impressionLogging.handleSortOptionChange(eventId);
        }

        if (eventDataObjectType === 'filter') {
          impressionLogging.handleFilterOptionChange(eventId);

          const gtmData = {
            event: 'eventTracked',
            eventAction: 'filter',
            eventCategory: 'flights',
            eventLabel: '',
          };

          gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
        }
      }
    },
    [
      selectedFiltersObject,
      pageViewId,
      user?.userHash,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      gtmAuth,
      gtmPreview,
    ],
  );

  const { genzoTrackApplyFilterSearchView, genzoTrackApplySortSearchView } =
    useFlightSearchViewGenzoTracking({
      selectedFiltersObject,
      pageViewId,
      visibleTripCards:
        sortType === 'price' ? visibleTripCardForGroupedTrips : visibleTripCardForUngroupedTrips,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    });

  const handleOnSortTypeChanged = useCallback<
    (flightSortType: FlightSortType, sortOrder: SortOrder) => void
  >(
    (flightSortType: FlightSortType, sortOrder: SortOrder) => {
      recordChangeSortAction();

      changeSort(flightSortType, sortOrder);

      let gtmLabel: string | undefined;

      genzoTrackApplySortSearchView();
      switch (flightSortType) {
        case 'duration':
          genzoTrackApplyFilterActionEvent('sort', 'applied', {
            current_sort: FlightSelectedSortTypeForGenzoTracking.DURATION,
          });
          gtmLabel = 'duration';
          break;
        case 'price':
          genzoTrackApplyFilterActionEvent('sort', 'applied', {
            current_sort: FlightSelectedSortTypeForGenzoTracking.PRICE,
          });
          gtmLabel = 'price';
          break;
        case 'score':
          genzoTrackApplyFilterActionEvent('sort', 'applied', {
            current_sort: FlightSelectedSortTypeForGenzoTracking.SCORE,
          });
          gtmLabel = 'best-experience';
          break;
        case 'leg2_departure_time':
          genzoTrackApplyFilterActionEvent('sort', 'applied', {
            current_sort: FlightSelectedSortTypeForGenzoTracking.FLIGHT_TIME,
          });
          if (isRoundTrip) {
            gtmLabel = sortOrder === 'asc' ? 'e-ar' : 'l-ar';
          } else {
            gtmLabel = sortOrder === 'asc' ? 'e-dep' : 'l-dep';
          }
          break;

        case 'leg1_departure_time':
        case 'leg3_departure_time':
        case 'leg4_departure_time':
        case 'leg5_departure_time':
        case 'leg6_departure_time':
          genzoTrackApplyFilterActionEvent('sort', 'applied', {
            current_sort: FlightSelectedSortTypeForGenzoTracking.FLIGHT_TIME,
          });
          gtmLabel = sortOrder === 'asc' ? 'e-dep' : 'l-dep';
          break;

        default:
          break;
      }

      if (gtmLabel) {
        const gtmData = {
          event: 'eventTracked',
          eventAction: 'sort',
          eventCategory: 'flights',
          eventLabel: gtmLabel,
        };

        gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
      }
    },
    [
      changeSort,
      genzoTrackApplyFilterActionEvent,
      genzoTrackApplySortSearchView,
      gtmAuth,
      gtmPreview,
      isRoundTrip,
      recordChangeSortAction,
    ],
  );

  const handleOnFlightFiltersReset = useCallback<() => void>(() => {
    let updatedSearchParams;

    if (isPerLegSearch && activeLeg === 1) {
      updatedSearchParams = removeReturnLegFilterParams(searchParams);
    } else {
      updatedSearchParams = deleteFilterStatesFromSearchParam(searchParams).toString();
    }

    setSelectedLegs([]);

    setSearchParams(updatedSearchParams, { replace: true, state });

    genzoTrackApplyFilterActionEvent('filter', 'reset', {});
    genzoTrackApplyFilterSearchView();
  }, [
    genzoTrackApplyFilterActionEvent,
    genzoTrackApplyFilterSearchView,
    searchParams,
    setSearchParams,
    state,
  ]);

  const handleRefreshSearch = useCallback<() => void>(() => {
    handleCloseTripDetailPopup();
    dispatch(updateTriggerSearchCounter(triggerSearchCounter + 1));
  }, [dispatch, handleCloseTripDetailPopup, triggerSearchCounter]);

  // Used With Per Leg Search
  useEffect(() => {
    if (isPerLegSearch) {
      if (activeLeg === 1) {
        copyFilterParamsFromDepartLegToReturnLeg(searchParams);
        copySortParamsFromDepartLegToReturnLeg(searchParams);
      } else {
        removeReturnLegFilterParams(searchParams);
        removeReturnLegSortParams(searchParams);
      }
      setSearchParams(searchParams, { replace: true, state });
    }
  }, [isPerLegSearch, activeLeg]);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Abort any active recordings when a new search is triggered
    // Assuming that the component is remounted for each search
    return () => {
      abortFlightSearchResultRecordings();
    };
  }, []);

  // Add payment method to URL if they are not there
  useEffect(() => {
    if (paymentMethods.length === 0) {
      const filterAvailablePaymentMethodsByPopular = availablePaymentMethods?.length
        ? availablePaymentMethods.filter((apm) => apm.isPopular)
        : [];

      const listOfPaymentMethodIds =
        filterAvailablePaymentMethodsByPopular.length > 0
          ? filterAvailablePaymentMethodsByPopular.map((apm) => apm.id)
          : [];

      if (listOfPaymentMethodIds.length > 0) {
        searchParams.set('payment_methods', listOfPaymentMethodIds.toString());

        setSearchParams(searchParams, { replace: true });
      }

      return;
    }
  }, [availablePaymentMethods, paymentMethods.length, searchParams, setSearchParams]);

  // If user pass the challenge, reset the search after 1 second, with cf_clearance cookie is set
  const handleTurnstileVerify = () => {
    setTimeout(() => {
      handleRefreshSearch();
    }, 1000);
  };

  // WebEngage Tracking
  useFlightSearchWebEngageTracking();

  useEffect(() => {
    if (isPerLegSearch) {
      if (isPLSDepartLegSelection) {
        setActiveLeg(0);
      } else {
        setActiveLeg(1);
      }
    }
  }, [isPLSDepartLegSelection, isPerLegSearch, setActiveLeg]);

  // Genzo flight search, page view and visits tracking
  useFlightResultGenzoTracking({ pageViewId, wegoAnalyticsClientId, wegoAnalyticsClientSessionId });

  // GTM tracking
  useFlightSearchGTMTracking({
    nearestCityByIP,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
  });

  // Expire search dialog timeout
  useEffect(() => {
    if (search?.createdAt) {
      const flightMetaSearchExpireByMilliseconds =
        new Date(search.createdAt).getTime() + 1800 * 1000; // 1,800,000 milliseconds = 30mins
      const nowMilliseconds = Date.now();

      if (flightMetaSearchExpireByMilliseconds > nowMilliseconds) {
        const refreshSearchTimeout = setTimeout((): void => {
          setIsSearchExpired(true);
        }, flightMetaSearchExpireByMilliseconds - nowMilliseconds);

        return () => {
          clearTimeout(refreshSearchTimeout);
        };
      }
    }
  }, [search?.createdAt]);

  // Save recent search for comparing
  useEffect(() => {
    if (search && cheapestFareViewModel && bestExperienceCheapestFareViewModel) {
      saveFlightRecentSearchForComparing({
        locale,
        adultCount: search.adultsCount,
        cabinClass: search.cabin,
        childCount: search.childrenCount,
        infantCount: search.infantsCount,
        legSearches: search.legs.map((leg) => ({
          id: leg.id,
          outboundDate: leg.outboundDate,
          arrivalCityCode: leg.arrivalCity.code,
          departureCityCode: leg.departureCity.code,
          arrivalAirportCode: leg.arrivalAirport?.code,
          departureAirportCode: leg.departureAirport?.code,
        })),
        paymentMethods,
        searchId: search.id,
        prices: {
          cheapest: {
            perPerson: convertFlightSearchResultFarePriceToPrice(
              cheapestFareViewModel.fare.price,
              passengerCount,
              false,
              true,
            ),
            totalPrice: convertFlightSearchResultFarePriceToPrice(
              cheapestFareViewModel.fare.price,
              passengerCount,
              true,
              true,
            ),
          },
          best: {
            perPerson: convertFlightSearchResultFarePriceToPrice(
              bestExperienceCheapestFareViewModel.fare.price,
              passengerCount,
              false,
              true,
            ),
            totalPrice: convertFlightSearchResultFarePriceToPrice(
              bestExperienceCheapestFareViewModel.fare.price,
              passengerCount,
              true,
              true,
            ),
          },
        },
      });
    }
  }, [
    bestExperienceCheapestFareViewModel,
    cheapestFareViewModel,
    search,
    passengerCount,
    paymentMethods,
    locale,
  ]);

  const handleFlightSearchResultOnBack = useCallback((): void => {
    searchParams.delete('payment_methods');
    searchParams.delete('sort');
    searchParams.delete('order');
    searchParams.delete('flexibilities');
    searchParams.delete('full_card');

    navigate(
      {
        pathname: flightsRoute(currentSite, locale),
        search: deleteFilterStatesFromSearchParam(searchParams).toString(),
      },
      { replace: true, state },
    );
  }, [currentSite, locale, navigate, searchParams, state]);

  return (
    <div className={clsx(styles.container, isRtl && styles.rtl)}>
      <AdsBlockerBait />

      {showTurnstileWidget && <TurnstileWidget onVerify={handleTurnstileVerify} />}

      {/* Put error here */}
      {showErrorMessage && !showTurnstileWidget && (
        <AlertMessage
          type={errorAlertContents.type}
          className={styles.alertWrapper}
          title={errorAlertContents.title}
          message={errorAlertContents.message}
          buttonLabel={errorAlertContents.buttonLabel}
          error={flightMetaSearchOriginalError}
          onClick={() => navigate(0)}
        />
      )}

      {/* Refresh popup */}
      {isSearchExpired ? (
        <RefreshPopup
          genzoTrackPageViewBaseType='flights_search_results'
          localeParam={localeParam}
          onRefresh={handleRefreshSearch}
          wegoAnalyticsClientId={wegoAnalyticsClientId}
          wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
        />
      ) : null}

      {/* Per Leg Search Stepper */}
      {isPerLegSearch && (
        <FlightPerLegSearchStepper
          wegoAnalyticsClientId={wegoAnalyticsClientId}
          wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
        />
      )}

      {isFlightSearch2AndNotMulticity && (
        <SortAndFilterBar
          availablePaymentMethods={availablePaymentMethods}
          genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
          genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
          isAllFiltersOpen={isAllFiltersOpen}
          onSortTypeChanged={handleOnSortTypeChanged}
          setIsAllFiltersOpen={setIsAllFiltersOpen}
        />
      )}

      {/* Result and Filter */}
      {!showErrorMessage && !showTurnstileWidget && (
        <div
          className={clsx(
            styles.filterAndResultContainer,
            isFlightSearch2AndNotMulticity && styles.isFlightSearch2,
          )}
        >
          <FlightFilters
            className={styles.filter}
            isAllFiltersOpen={isAllFiltersOpen}
            onReset={handleOnFlightFiltersReset}
            onAllFiltersClose={() => setIsAllFiltersOpen(false)}
            genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
            genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
            availablePaymentMethods={availablePaymentMethods}
          />

          <div className={styles.result}>
            <FlightSearchResultContentAds
              bnplToolTipShown={bnplToolTipShown}
              bottomLeaderboardAdsHeight={bottomLeaderboardAdsHeight}
              leaderboardAdsAndInlineAdsConfig={leaderboardAdsAndInlineAdsConfig}
              middleLeaderboardAdsHeight={middleLeaderboardAdsHeight}
              nativeBottomAdHeight={nativeBottomAdHeight}
              nativeMiddleAdHeight={nativeMiddleAdHeight}
              nativeTopAdHeight={nativeTopAdHeight}
              topLeaderboardAdsHeight={topLeaderboardAdsHeight}
            />

            {hasEgyptCentralBankNotice ? (
              <div className={styles.egyptCentralBankNotice}>
                {translations.eg_central_bank_fee_text}
              </div>
            ) : null}

            {finalTripViewModelsToShowBasedOnSortType.length === 0 ? null : (
              <>
                {!isFlightSearch2AndNotMulticity && (
                  <FlightSort onSortTypeChanged={handleOnSortTypeChanged} />
                )}

                <ProgressBar
                  wegoAnalyticsClientId={wegoAnalyticsClientId}
                  wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
                />
              </>
            )}

            <FlightSearchResultList
              availablePaymentMethods={availablePaymentMethods}
              bottomLeaderboardAdsHeight={bottomLeaderboardAdsHeight}
              genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
              genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              impressionLogging={impressionLogging}
              leaderboardAdsAndInlineAdsConfig={leaderboardAdsAndInlineAdsConfig}
              middleLeaderboardAdsHeight={middleLeaderboardAdsHeight}
              nativeBottomAdHeight={nativeBottomAdHeight}
              nativeMiddleAdHeight={nativeMiddleAdHeight}
              nativeTopAdHeight={nativeTopAdHeight}
              onTripCardMouseHover={setBnplTooltipShown}
              onTripViewModelSelect={setSelectedTripViewModel}
              setVisibleTripCardForGroupedTrips={setVisibleTripCardForGroupedTrips}
              setVisibleTripCardForUngroupedTrips={setVisibleTripCardForUngroupedTrips}
              topLeaderboardAdsHeight={topLeaderboardAdsHeight}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            />

            {/* Search results loading message box */}
            {!isFlightSearch2AndNotMulticity &&
            isLoading &&
            finalTripViewModelsToShowBasedOnSortType.length === 0 ? (
              <div className={styles.searchLoadingMessageBox}>
                {translations.flights_loading}

                <Spinner className={styles.spinner} />
              </div>
            ) : null}

            {/* No Results */}
            {tripViewModels.length > 0 &&
            !isLoading &&
            !isPLSChangeLegLoading &&
            finalTripViewModelsToShowBasedOnSortType.length === 0 ? (
              <NoResults
                onMount={abortFlightSearchResultRecordings}
                buttonLabel={translations.reset as string}
                className={styles.noResult}
                message={translations.no_results as string}
                onClick={handleOnFlightFiltersReset}
                title={translations.no_result_found as string}
              />
            ) : null}

            {search && tripViewModels.length === 0 && !isLoading && !isPLSChangeLegLoading ? (
              <NoResults
                onMount={abortFlightSearchResultRecordings}
                buttonLabel={translations.go_back as string}
                className={styles.noResult}
                message={translations.sorry_no_results as string}
                onClick={handleFlightSearchResultOnBack}
                title={translations.no_results_header as string}
              />
            ) : null}
          </div>

          {!isFlightSearch2AndNotMulticity && (
            <FlightResultRHS
              isShowingMrecOrTopStickyAds={hasMrecOrTopStickyAds && tripViewModels.length > 0}
              gtmAuth={gtmAuth}
              gtmPreview={gtmPreview}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            />
          )}
        </div>
      )}

      {/* Overlay for TripDetail popup */}
      <div
        id='overlay'
        className={clsx(styles.tripDetailOverlay, !!selectedTripIdSearchParam && styles.open)}
        onClick={handleCloseTripDetailPopup}
      >
        {selectedTripIdSearchParam ? (
          <TripDetail
            clientId={wegoAnalyticsClientId}
            clientSessionId={wegoAnalyticsClientSessionId}
            gtmAuth={gtmAuth}
            gtmPreview={gtmPreview}
            handleOnOverlayClick={handleCloseTripDetailPopup}
            localeParam={localeParam}
            nearestCityByIP={nearestCityByIP}
            paymentMethodsFromApi={availablePaymentMethods}
            tripViewModel={selectedTripViewModel}
          />
        ) : null}
      </div>

      <HotelCrossSell
        pageViewId={pageViewId}
        isOpen={showCrossSellPopup}
        setIsOpen={setShowCrossSellPopup}
        wegoAnalyticsClientId={wegoAnalyticsClientId!}
        wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId!}
      />

      <BackToTopButton onScrollToTop={handleScrollToTop} />
    </div>
  );
};

const FlightSearchResultWrapper = (
  props: Omit<FlightSearchResultProps, 'onSearchIdChange'>,
): JSX.Element => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locale = useSelector(getLocaleState);
  const currency = useSelector(getCurrencyState);
  const user = useSelector(getUserState);
  const triggerSearchCounter = useSelector(getTriggerSearchCounterState);

  const resetSearchFactors = [
    triggerSearchCounter,
    location.pathname,
    searchParams.get('payment_methods'),
    locale,
    currency.code,
    user?.email,
  ];

  return (
    <>
      <FlightSearchFiltersProvider
        // Force remount when params are changed to reset results
        key={resetSearchFactors.toString()}
      >
        <FlightPerLegSearchProvider>
          <FlightSearchResultsProvider
            gtmAuth={props.gtmAuth}
            gtmPreview={props.gtmPreview}
            wegoAnalyticsClientId={props.wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={props.wegoAnalyticsClientSessionId}
          >
            <FlightSearchResult$ {...props} />
          </FlightSearchResultsProvider>
        </FlightPerLegSearchProvider>
      </FlightSearchFiltersProvider>
    </>
  );
};

export default FlightSearchResultWrapper;
